import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as Page from '../../components/content/page-header';
import { Danger, LightBlackSmall, Tertiary } from '../../components/button/button';
import { Label, TextInputLarge } from '../../components/forms/input';
import { Modes } from '../../types/page-modes';
import { FilteredLayer, MapLayer } from './types/map-layer';
import usePageState from '../../hooks/use-page-state';
import { Table } from '../../components/table/table';
import { LayerGroup, LayerGroupLayer } from './types/layer-group';
import { useQuery } from '../../hooks/use-query';
import { Icons } from '../../components/icons/icons';
import { Actions, Content as DialogContent, Dialog, Title } from '../../components/dialog/dialog';
import { useDeleteMutation, usePostMutation } from '../../hooks/use-mutations';
import { ActionColumn } from '../../components/table/columns';
import { LayerFilter } from '../../widgets/filter-layers';

export const LayerGroupEditor = (): JSX.Element => {
  const [pageState, setPageState] = usePageState();
  const navigate = useNavigate();
  const params = useParams();
  const groupId = params.id as string;
  const [selectedLayers, setSelectedLayers] = useState<string[]>([]);
  const layerGroup = useQuery<LayerGroup>({ path: `/layer-groups/${groupId}` });
  const deleteMutation = useDeleteMutation<MapLayer>({ cacheKey: `/layer-groups/${groupId}` });
  const postMutation = usePostMutation<FilteredLayer>({ cacheKey: `/layer-groups/${groupId}` });

  useEffect(() => {
    if (layerGroup.isSuccess) {
      setSelectedLayers(layerGroup.data?.layers?.map(item => item.id));
    }
  }, [layerGroup.isSuccess]);

  const onAdd = (item: FilteredLayer): void => {
    setSelectedLayers([...selectedLayers, item.id]);
    postMutation.mutate({ path: `/layer-groups/${groupId}/layers/${item.id}` });
  };

  const onDeleteConfirm = (): void => {
    setSelectedLayers(selectedLayers.filter(id => id !== pageState.item?.id));
    deleteMutation.mutate({ path: `/layer-groups/${groupId}/layers/${pageState.item?.id as string}` });
    setPageState(Modes.BROWSE);
  };

  const onDelete = (item: LayerGroupLayer): void => {
    setPageState(Modes.DELETE, item);
  };

  const existingLayersColumns = [
    { accessor: 'sharedId', Header: 'Shared Id' },
    { accessor: 'name', Header: 'Name' },
    { accessor: 'type', Header: 'Type' },
    ActionColumn({ onDelete })
  ];

  return <div>
    <Page.Header>
      <Page.Tenant />
      <Page.Title>Layer groups</Page.Title>
      <Page.SubTitle>Layer Groups define groups of layers where only one layer can be turned on at a time.</Page.SubTitle>
    </Page.Header>
    <div className="row">
      <div className="col-9 bg-light left-col">
        <div className="mb-4">
          <Label name="name">Layer Group Id</Label>
          <TextInputLarge value="asd" disabled />
        </div>
        <div className="mb-3 mt-3 d-flex justify-content-between">
          <span />
          <LightBlackSmall onClick={(): void => setPageState(Modes.CREATE)}>
            <Icons.Add />
            Add Layer
          </LightBlackSmall>
        </div>
        <Table
          columns={existingLayersColumns}
          rows={layerGroup.data?.layers || []}
          onDelete={(item): void => setPageState(Modes.DELETE, item)}
        />
      </div>
      <div className="col-3 d-flex flex-column">
        <div className="px-5">
          <div className="mb-3">
            <Tertiary onClick={(): void => navigate('/layer-groups')}>
              Cancel
            </Tertiary>
          </div>
        </div>
      </div>
    </div>
    {pageState.mode === Modes.CREATE && (
      <LayerFilter
        title="Add Layer"
        onAdd={onAdd}
        selectedLayers={selectedLayers}
        onClose={(): void => setPageState(Modes.BROWSE)}
      />
    )}
    {pageState.mode === Modes.DELETE && (
      <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} backdropClose={false}>
        <Title>Delete Layer</Title>
        <DialogContent>Are you sure you want to delete?</DialogContent>
        <Actions>
          <Tertiary onClick={(): void => setPageState(Modes.BROWSE)}>Cancel</Tertiary>
          <Danger onClick={onDeleteConfirm}>Delete</Danger>
        </Actions>
      </Dialog>
    )}
  </div>;
};
